import { useMemo } from "react";
import type { LogStats } from "@/api/client";

interface LogsVolumeChartProps {
  stats: LogStats;
  rangeLabel: string;
}

const LEVEL_COLORS: Record<string, string> = {
  ERROR: "#ef4444",
  FATAL: "#ef4444",
  CRITICAL: "#dc2626",
  WARN: "#eab308",
  WARNING: "#eab308",
  INFO: "#10b981",
  DEBUG: "#3b82f6",
  TRACE: "#a855f7",
};

/**
 * Compact stacked sparkline of log volume over time, grouped by level.
 * Used in History mode to give a quick visual of error spikes.
 */
export function LogsVolumeChart({ stats, rangeLabel }: LogsVolumeChartProps) {
  const sorted = useMemo(() => {
    const byTime = new Map<string, { time: string; total: number; byLevel: Record<string, number> }>();
    for (const b of stats.buckets) {
      const lvl = (b.level || "INFO").toUpperCase();
      const existing = byTime.get(b.time);
      if (existing) {
        existing.total += Number(b.count);
        existing.byLevel[lvl] = (existing.byLevel[lvl] ?? 0) + Number(b.count);
      } else {
        byTime.set(b.time, {
          time: b.time,
          total: Number(b.count),
          byLevel: { [lvl]: Number(b.count) },
        });
      }
    }
    return Array.from(byTime.values()).sort((a, b) => a.time.localeCompare(b.time));
  }, [stats.buckets]);

  if (sorted.length === 0) return null;

  const maxCount = Math.max(...sorted.map((b) => b.total), 1);
  const chartH = 56;
  const totalEvents = sorted.reduce((sum, b) => sum + b.total, 0);
  const barWidth = Math.max(2, Math.floor(800 / sorted.length) - 1);

  return (
    <div className="rounded-md border bg-card overflow-hidden">
      <div className="flex items-center justify-between px-3 pt-2 text-[10px] text-muted-foreground">
        <span>Объём</span>
        <span>событий: {totalEvents.toLocaleString("ru-RU")} · {rangeLabel}</span>
      </div>
      <svg
        width="100%"
        height={chartH}
        viewBox={`0 0 ${sorted.length * (barWidth + 1)} ${chartH}`}
        preserveAspectRatio="none"
      >
        {sorted.map((bucket, i) => {
          let y = chartH;
          const rects: React.ReactNode[] = [];
          const orderedLevels = ["DEBUG", "TRACE", "INFO", "WARN", "ERROR", "FATAL"];
          const ordered = Object.entries(bucket.byLevel).sort(([a], [b]) => {
            return orderedLevels.indexOf(a) - orderedLevels.indexOf(b);
          });
          for (const [level, count] of ordered) {
            const segH = (count / maxCount) * (chartH - 4);
            y -= segH;
            rects.push(
              <rect
                key={`${i}-${level}`}
                x={i * (barWidth + 1)}
                y={y}
                width={barWidth}
                height={segH}
                fill={LEVEL_COLORS[level] ?? "#6b7280"}
                opacity={0.8}
              />
            );
          }
          return <g key={i}>{rects}</g>;
        })}
      </svg>
    </div>
  );
}
